import {
  Box,
  Button,
  Container,
  Input,
  Stack,
  StackDivider,
  Text,
  useBreakpointValue,
  useColorMode,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { useAtomValue } from 'jotai';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { styled } from '../Stitches';
import { serverUrl } from '../config';
import { formatComposer, formatPreComposer, routes } from '../data/routes';
import { tokenAtom } from '../lib/Session';
import Emoji from './Emoji';
import { Fade } from './Fade';
import { Fetching } from './Fetching';
import { useFilter } from './hooks/useFilter';

const fetchFriends = async (token: string) => {
  const fetchUrl = `${serverUrl}/api/friends`;
  const request = {
    method: 'get',
    headers: { authorization: `Token ${token}` },
  };
  return await fetch(fetchUrl, request).then((res) => res.json());
};

interface Friend {
  id: string;
  firstName: string;
  lastName: string;
  draftId?: string;
}

type FriendPickerProps = { friends: Friend[] };

const FriendPickerContainer = (props: { children: React.ReactNode }) => {
  const { colorMode } = useColorMode();
  const boxShadow = useBreakpointValue([undefined, 'base']);
  return (
    <Container
      bg={colorMode === 'dark' ? 'whiteAlpha.50' : 'white'}
      boxShadow={boxShadow}
      w="sm"
      p={8}
      rounded="xl"
    >
      {props.children}
    </Container>
  );
};

type FriendRowProps = { friend: Friend; onClick: () => void };

const FriendRow = ({ friend, onClick }: FriendRowProps) => (
  <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 1 }} onClick={onClick}>
    <Box borderWidth="1px" p={2} rounded="base">
      <Text key={friend.id}>
        {friend.firstName} {friend.lastName}
      </Text>
    </Box>
  </motion.div>
);

const InviteFriendsButton = () => (
  <Link to={routes.invitations}>
    <Button>
      <Emoji symbol="👋" label="hand-wave" spaceRight /> Invite a friend to write to you
    </Button>
  </Link>
);

const EmptyFriendPicker = () => (
  <Stack direction="column" alignItems="center" spacing={4}>
    <Text fontSize="lg">You don&apos;t currently have any pen pals</Text>
    <InviteFriendsButton />
  </Stack>
);

const useSelectFriend = () => {
  const navigate = useNavigate();

  return useCallback(
    (friendId: string, letterId: string | undefined) => {
      if (letterId) {
        navigate(formatComposer({ letterId }));
      } else {
        navigate(formatPreComposer({ friendId }));
      }
    },
    [navigate]
  );
};

const Super = styled('span', {
  position: 'relative',
  top: '-$6',
});

const InviteWithLetter = () => (
  <Link to={routes.inviteWithLetter}>
    <Button>Invite new friends with a letter </Button>
    <Super>
      <Emoji
        symbol="✨"
        label="Emoji of a sparkle indicating that the invite with a letter button is new"
      />
    </Super>
  </Link>
);

const FriendPicker = ({ friends }: FriendPickerProps) => {
  const [query, setQuery] = useState('');
  const filteredFriends = useFilter(
    (f: Friend) => [`${f.firstName} ${f.lastName}`],
    friends,
    query
  );

  const selectFriend = useSelectFriend();

  return (
    <Stack direction="column" spacing={4}>
      <InviteWithLetter />
      <StackDivider borderWidth={1} borderColor="" />
      <Text>
        <Emoji symbol="✍️" spaceRight /> Who are you writing to today?
      </Text>
      <Input placeholder="Search friends" onChange={(e) => setQuery(e.target.value)} />
      <StackDivider borderWidth={1} borderColor="purple.300" />
      <Stack>
        {filteredFriends &&
          filteredFriends.map((f) => (
            <FriendRow key={f.id} friend={f} onClick={() => selectFriend(f.id, f.draftId)} />
          ))}
      </Stack>
    </Stack>
  );
};

const useFetchFriends = () => {
  const token = useAtomValue(tokenAtom);
  const [friends, setFriends] = useState<any>(undefined);
  const [isFetching, setIsFetching] = useState(true);

  useEffect(() => {
    if (token) {
      fetchFriends(token).then((data) => {
        setFriends(data);
        setIsFetching(false);
      });
    }
  }, [token, setFriends, setIsFetching]);

  return { friends, isFetching };
};

export const NewLetter = () => {
  const { friends, isFetching } = useFetchFriends();

  return (
    <Fade>
      <FriendPickerContainer>
        <Fetching isFetching={isFetching}>
          {friends && friends.length === 0 ? (
            <EmptyFriendPicker />
          ) : (
            <FriendPicker friends={friends} />
          )}
        </Fetching>
      </FriendPickerContainer>
    </Fade>
  );
};
